import { Outlet, Link, useNavigate } from 'react-router-dom';
import { Suspense, useState, useEffect } from 'react';
import { api } from '@/lib/api';
import { Menu, X } from 'lucide-react';
import { ScrollToTop } from '@/components/ScrollToTop';

export function PublicLayout() {
  const [navItems, setNavItems] = useState<{ label: string; href: string }[]>([]);
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();

  const [footerCols, setFooterCols] = useState<{ title: string; links: { label: string; href: string }[] }[]>([]);

  useEffect(() => {
    api.navigation().then(setNavItems).catch(() => {});
    api.footer().then(setFooterCols).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen flex flex-col" style={{ background: 'var(--background)', color: 'var(--foreground)' }}>
      <ScrollToTop />
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-[100] focus:rounded-lg focus:bg-purple-600 focus:px-4 focus:py-2 focus:text-white"
      >
        Перейти к содержимому
      </a>
      <header className="sticky top-0 z-50 border-b backdrop-blur-md" style={{ borderColor: 'var(--border)', background: 'color-mix(in srgb, var(--background) 85%, transparent)' }}>
        <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-4 md:px-6">
          <Link to="/" className="text-lg font-semibold tracking-tight">
            SHOT <span style={{ color: 'var(--accent)' }}>BRO</span>
          </Link>
          <nav className="hidden gap-6 text-sm md:flex" aria-label="Основное меню">
            {navItems.map((item) => (
              <Link key={item.href} to={item.href} className="text-[var(--muted-foreground)] transition hover:text-[var(--foreground)]">
                {item.label}
              </Link>
            ))}
          </nav>
          <div className="flex items-center gap-2 md:gap-3">
            <button
              type="button"
              className="md:hidden p-2 rounded-lg border cursor-pointer min-h-[44px] min-w-[44px] flex items-center justify-center"
              style={{ borderColor: 'var(--border)' }}
              aria-expanded={mobileOpen}
              aria-label={mobileOpen ? 'Закрыть меню' : 'Открыть меню'}
              onClick={() => setMobileOpen((o) => !o)}
            >
              {mobileOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
            <button
              type="button"
              onClick={() => navigate('/contacts')}
              className="hidden sm:inline-flex rounded-full px-4 py-2 text-sm font-medium cursor-pointer transition hover:opacity-90 min-h-[44px] items-center"
              style={{ background: 'var(--primary)', color: 'var(--primary-foreground)' }}
            >
              Обсудить проект
            </button>
          </div>
        </div>
        {mobileOpen && (
          <nav className="md:hidden border-t px-4 py-3 space-y-2" style={{ borderColor: 'var(--border)' }} aria-label="Мобильное меню">
            {navItems.map((item) => (
              <Link
                key={item.href}
                to={item.href}
                onClick={() => setMobileOpen(false)}
                className="block py-2 text-sm text-[var(--muted-foreground)] min-h-[44px]"
              >
                {item.label}
              </Link>
            ))}
            <button
              type="button"
              onClick={() => {
                setMobileOpen(false);
                navigate('/contacts');
              }}
              className="w-full rounded-full py-3 text-sm font-medium cursor-pointer min-h-[44px]"
              style={{ background: 'var(--primary)', color: 'var(--primary-foreground)' }}
            >
              Обсудить проект
            </button>
          </nav>
        )}
      </header>
      <main id="main-content" className="flex-1" tabIndex={-1}>
        <Suspense fallback={
          <div className="flex items-center justify-center min-h-[60vh]">
            <div className="animate-pulse text-[var(--muted-foreground)]">Загрузка…</div>
          </div>
        }>
          <Outlet />
        </Suspense>
      </main>
      <footer className="border-t py-10 text-sm text-[var(--muted-foreground)]" style={{ borderColor: 'var(--border)' }}>
        <div className="mx-auto max-w-6xl px-4 md:px-6 grid gap-8 md:grid-cols-3">
          <div>
            <p className="font-medium text-[var(--foreground)]">SHOT BRO</p>
            <p className="mt-2">Твоё воображение. Наше воплощение.</p>
            <p className="mt-4">© {new Date().getFullYear()} · AI-видео и мультфильмы</p>
          </div>
          {footerCols.map((col) => (
            <div key={col.title}>
              <p className="font-medium text-[var(--foreground)] mb-2">{col.title}</p>
              <ul className="space-y-1">
                {(col.links || []).map((link) => (
                  <li key={link.href}>
                    <Link to={link.href} className="hover:text-[var(--foreground)] transition">
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </footer>
    </div>
  );
}